import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { api, API_STATUS_EVENT } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './Layout.css';
import './TopbarFix.css';

export const Layout = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [apiOffline, setApiOffline] = useState(false);
  const [checking, setChecking] = useState(false);

  const checkConnection = useCallback(async () => {
    setChecking(true);
    try {
      await api.health();
      setApiOffline(false);
    } catch (error) {
      if (error.code === 'API_UNAVAILABLE') setApiOffline(true);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (user?.global_access) return undefined;
    const area = location.pathname.startsWith('/members') ? 'members'
      : location.pathname.startsWith('/community') ? 'comments'
      : location.pathname.startsWith('/finances') ? 'payments' : null;
    if (!area || (user?.role === 'Alumno' && area !== 'comments')) return undefined;
    let live = true;
    api.notifications.markAttentionReviewed(area).then(() => {
      if (!live) return;
      window.dispatchEvent(new Event('pulsora:attention-updated'));
      window.dispatchEvent(new Event('pulsora:notifications-updated'));
    }).catch(() => {});
    return () => { live = false; };
  }, [location.pathname, user?.active_center?.centro_id, user?.global_access, user?.role]);

  useEffect(() => {
    const onApiStatus = event => setApiOffline(!event.detail.online);
    window.addEventListener(API_STATUS_EVENT, onApiStatus);
    checkConnection();
    const interval = window.setInterval(checkConnection, 15000);
    return () => {
      window.removeEventListener(API_STATUS_EVENT, onApiStatus);
      window.clearInterval(interval);
    };
  }, [checkConnection]);

  const centerTheme = !user?.global_access && user?.role !== 'Alumno' && user?.active_center ? { '--center-primary': user.active_center.color_primario || '#1457F4', '--center-secondary': user.active_center.color_secundario || '#0C1740' } : undefined;

  return (
    <div className={`app-layout${centerTheme ? ' center-themed' : ''}`} style={centerTheme}>
      <div className="app-topbar">
        <Sidebar />
        <Header />
      </div>
      <div className="main-content">
        {apiOffline && (
          <div className="api-status-banner" role="alert">
            <AlertTriangle size={20} />
            <div>
              <strong>No se pudo conectar con Pulsora</strong>
              <span>El servidor de la aplicación no está respondiendo. La conexión se comprobará automáticamente.</span>
            </div>
            <button type="button" onClick={checkConnection} disabled={checking}>
              <RefreshCw size={17} className={checking ? 'is-spinning' : ''} />
              {checking ? 'Comprobando…' : 'Reintentar'}
            </button>
          </div>
        )}
        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
